import { useEffect, useRef, type MutableRefObject } from 'react';
import { elementMark, elementTextProp } from './editorElement';
import { isChrome, markAt } from './editorVisit';

/**
 * Typing straight into the page. A double-click on a node that holds words — a `heading`, a `paragraph`,
 * anything whose renderer named a text prop — or on a marked field of a band makes it editable; Enter or
 * a click away commits, Escape throws the edit away. What is committed goes up as `typed` and nothing
 * else happens here — the builder owns the draft, and the canvas never becomes a second copy of it.
 */

/** The class the field being typed into carries, so the builder's own ring is drawn on it. */
const EDITING_CLASS = 'xs-editor-editing';

/**
 * Where an edit is written back to: the node or the section that owns it, and the prop or the settings
 * path inside it. One pair for both engines, because `typed` carries exactly one pair — a node names
 * itself and the prop its words live in, a band names itself and the field the theme marked.
 */
export type InlineOwner = { ownerId: string; path: string };

/** The live edit: the element made editable, where its value belongs, and what it said before. */
export type InlineTarget = InlineOwner & { element: HTMLElement; original: string };

/** How the canvas reports a committed edit; the builder answers with an `apply` carrying the new draft. */
export type TypedHandler = (ownerId: string, path: string, value: string) => void;

/** What the click handler reads to leave a click inside a live edit alone, so the caret can be placed. */
export type InlineRef = MutableRefObject<InlineTarget | null>;

/**
 * Reports the element one field's text is typed into: the mark itself, or the single element a theme
 * drew inside it when the mark is the boxless wrapper, which no browser will make an editing host.
 * Anything holding nested markup is refused — a settings path holds text, not a subtree.
 */
const editableHost = (mark: Element): HTMLElement | null => {
  const boxless = window.getComputedStyle(mark).display === 'contents';
  const host = boxless ? mark.firstElementChild : mark;
  return host instanceof HTMLElement && host.childElementCount === 0 ? host : null;
};

// Makes one field a plain-text editing host, so whatever is typed or dropped carries no formatting at all.
const beginEdit = (host: HTMLElement): void => {
  host.contentEditable = 'plaintext-only';
  if (host.contentEditable !== 'plaintext-only') host.contentEditable = 'true';
  host.spellcheck = false;
  host.classList.add(EDITING_CLASS);
  host.focus();
};

// Leaves the text plain again and, when the edit was kept and it actually changed, reports it.
const endEdit = (target: InlineTarget, keep: boolean, onTyped: TypedHandler): void => {
  const { element, original } = target;
  const value = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (!keep) element.textContent = original;
  element.removeAttribute('contenteditable');
  element.classList.remove(EDITING_CLASS);
  element.blur();
  if (keep && value !== original) onTyped(target.ownerId, target.path, value);
};

/**
 * Names what a double-click would type into: the node under the pointer, when its renderer said which
 * prop holds its words. A node whose content is not plain words — an image, a row — names no prop, so a
 * double-click on it opens nothing.
 */
const textOwnerAt = (target: EventTarget | null): (InlineOwner & { mark: Element }) | null => {
  const { elementId } = markAt(target);
  if (!elementId) return null;
  const path = elementTextProp(elementId);
  const mark = elementMark(elementId);
  return path && mark ? { ownerId: elementId, path, mark } : null;
};

// Opens an edit on one owner, answering the live target or null when what it marked holds no plain text.
const openEdit = (owner: InlineOwner & { mark: Element }): InlineTarget | null => {
  const host = editableHost(owner.mark);
  if (!host) return null;
  const target = { ownerId: owner.ownerId, path: owner.path, element: host, original: host.textContent ?? '' };
  beginEdit(host);
  return target;
};

// Wires the four listeners one inline edit needs, and answers how to take them all off again.
const attachInlineEdit = (ref: InlineRef, onTyped: TypedHandler): (() => void) => {
  const close = (keep: boolean) => {
    const target = ref.current;
    if (!target) return;
    ref.current = null;
    endEdit(target, keep, onTyped);
  };
  const onDoubleClick = (event: MouseEvent) => {
    if (isChrome(event.target)) return;
    const owner = textOwnerAt(event.target);
    if (!owner) return;
    close(true);
    const target = openEdit(owner);
    if (target) event.preventDefault();
    ref.current = target;
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (!ref.current) return;
    if (event.key === 'Enter' && !event.shiftKey) close(true);
    else if (event.key === 'Escape') close(false);
    else return;
    event.preventDefault();
  };
  const onFocusOut = (event: FocusEvent) => {
    if (ref.current && event.target === ref.current.element) close(true);
  };
  const onPaste = (event: ClipboardEvent) => {
    const host = ref.current?.element;
    if (!host || !(event.target instanceof Node) || !host.contains(event.target)) return;
    event.preventDefault();
    document.execCommand('insertText', false, (event.clipboardData?.getData('text/plain') ?? '').replace(/\s+/g, ' '));
  };
  document.addEventListener('dblclick', onDoubleClick, true);
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('focusout', onFocusOut, true);
  document.addEventListener('paste', onPaste, true);
  return () => {
    close(false);
    document.removeEventListener('dblclick', onDoubleClick, true);
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('paste', onPaste, true);
  };
};

// Lets a merchant type into the page itself, and hands every committed edit to the builder that owns the draft.
export function useInlineEdit(active: boolean, onTyped: TypedHandler): InlineRef {
  const editing = useRef<InlineTarget | null>(null);
  const handler = useRef(onTyped);
  handler.current = onTyped;

  useEffect(() => {
    if (!active) return undefined;
    return attachInlineEdit(editing, (ownerId, path, value) => handler.current(ownerId, path, value));
  }, [active]);

  return editing;
}
